// ============================================================================
// RÉGIME ET DATES — corriger la location d'un appareil déjà affecté, sans retour au parc (maquette du 28/10).
//
// UTILITÉ : rattraper une erreur d'affectation (une location courte qui était longue, une échéance mal saisie, une caisse
// affectée sans dates par l'assistant de mise en service). Ouverte par l'icône calendrier des Appareils et de l'onglet
// Équipements de la fiche magasin.
// ⚠️ LA RÈGLE EST CELLE DU SERVEUR (les deux dates pour une location, la fin après le début, aucune pour un achat) : l'écran
//    la montre pour fermer « Enregistrer », le serveur la refait.
// ⚠️ PAS D'AVERTISSEMENT DE CHEVAUCHEMENT, contrairement à la maquette : un appareil ne porte qu'une location, il n'y a
//    aucune réservation suivante avec laquelle comparer.
// ⚠️ CE QUE LA CORRECTION A FAIT SE DIT : la levée du blocage automatique d'une location en retard, et son échec de
//    descente aux caisses.
// ============================================================================
import { useId, useState, type CSSProperties, type MouseEvent } from 'react';
import { dateCompact } from '@orgazone/date-format';
import { alert, Icon, rovingRadio, showToast, useOverlayPanel } from '@orgazone/ui-web';
import type { IconName } from '@orgazone/ui-tokens';
import { api, type DeviceRow } from '../api';
import { glassBoxSt, glassOverlaySt } from '../glass-modal';
import { addBusinessDays, localDay, oneYearAfter, rentalDatesProblem, rentalDay, transitPhrase, type RentalRegime as Regime } from '../rental-days';
import { deviceRef } from '../device-name';

const REGIMES: { k: Regime; name: string; desc: string; icon: IconName }[] = [
  { k: 'courte', name: 'Location courte', desc: 'Quelques jours, sur le parc de location.', icon: 'clock' },
  { k: 'longue', name: 'Location longue', desc: 'Contrat au long cours, avec échéance.', icon: 'calendar' },
  { k: 'achat', name: 'Achat', desc: 'Propriété du client, sans échéance.', icon: 'tag' },
];
const NAME: Record<Regime, string> = { courte: 'location courte', longue: 'location longue', achat: 'achat' };

const show = (day: string): string => dateCompact(new Date(`${day}T12:00:00`));


/**
 * La modale « Régime et dates ». `transit` : les jours ouvrés reçus avec le parc (`transitOf`), `null` s'ils ne sont pas
 * connus — le retour au parc ne s'annonce alors pas.
 */
export function RentalModal({ dev, transit, onClose, onDone }: { dev: DeviceRow; transit: number | null; onClose: () => void; onDone: () => void }) {
  const wasRegime = dev.rentalType;
  const wasFrom = rentalDay(dev.rentalStart);
  const wasTo = rentalDay(dev.rentalEnd);
  const [regime, setRegime] = useState<Regime | null>(wasRegime);
  const [from, setFrom] = useState(wasFrom);
  const [to, setTo] = useState(wasTo);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const titleId = useId();
  const fromId = useId();
  const toId = useId();
  const { panelRef } = useOverlayPanel({ open: true, onClose, blockClose: () => busy });

  const dated = regime !== 'achat';
  const problem = rentalDatesProblem(regime, from, to);
  const changed = regime !== wasRegime || (dated ? from !== wasFrom || to !== wasTo : wasFrom !== '' || wasTo !== '');
  const ready = !problem && changed && !busy;
  const back = regime === 'courte' && !problem && transit !== null ? addBusinessDays(new Date(`${to}T12:00:00`), transit) : null;

  // Un changement de régime propose ses propres dates : un an pour une location longue, pas encore de retour pour une courte.
  const pick = (k: Regime) => {
    if (k === regime) return;
    setRegime(k);
    if (k === 'achat') return;
    const start = from || localDay(new Date());
    setFrom(start);
    setTo(k === 'longue' ? oneYearAfter(start) : '');
  };

  const save = async () => {
    if (!ready || !regime) return;
    setBusy(true); setErr(null);
    try {
      const r = await api.correctRental(dev.id, { rentalType: regime, start: dated ? from : null, end: dated ? to : null });
      onDone();
      onClose();
      if (r.lifted && !r.lifted.propagated) {
        await alert({
          title: 'Location corrigée — blocage levé, mais pas encore aux caisses',
          message: `Le blocage automatique de location est levé, mais sa levée n’a pas atteint : ${r.lifted.failures.map((f) => `${f.gln} (${f.error})`).join(', ')}. Elle sera reprise à la prochaine descente des blocages.`,
          tone: 'err',
        });
        return;
      }
      showToast(`Location corrigée — ${deviceRef(dev)}${r.lifted ? ' · blocage automatique levé' : ''}.`);
    } catch (e) { setErr((e as Error).message); setBusy(false); }
  };

  const info = regime === 'courte'
    ? `${transit === null ? 'Le transit n’a pas pu être lu : le retour au parc ne se calcule pas.'
      : back ? `Le transit compte ${transitPhrase(transit)} après le retour : l’appareil redevient disponible au parc le ${dateCompact(back)}.`
        : `Le transit compte ${transitPhrase(transit)} avant et après la location.`} La page Locations et le tableau de bord suivent.`
    : regime === 'longue'
      ? 'L’échéance déclenche l’alerte « échéance à moins de 30 jours » ; dépassée de 2 jours, l’appareil est bloqué automatiquement. Il sort des locations courtes.'
      : regime === 'achat'
        ? 'L’appareil reste au registre pour être paramétré à distance, mais ne compte plus dans le parc de location.'
        : 'Choisissez le régime de cet appareil.';
  const foot = err ?? (problem && regime ? problem : !changed ? 'Aucune modification.'
    : regime === 'courte' && back ? `${NAME.courte} · retour au parc le ${dateCompact(back)} · journalisé`
      : regime === 'longue' ? `${NAME.longue} · échéance le ${show(to)} · journalisé` : regime ? `${NAME[regime]} · sans échéance · journalisé` : '');
  const inputSt: CSSProperties = { display: 'block', width: '100%', height: 38, padding: '0 12px', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink200)', background: 'var(--elev)', color: 'var(--ink900)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-body)', outline: 'none' };
  const labelSt: CSSProperties = { display: 'block', fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink600)', marginBottom: 5 };
  const ctaOff: CSSProperties = { background: 'var(--ink200)', color: 'var(--ink400)', boxShadow: 'none', cursor: 'not-allowed' };
  const stop = (e: MouseEvent) => e.stopPropagation();

  return (
    <div style={glassOverlaySt} onClick={busy ? undefined : onClose}>
      <div style={{ ...glassBoxSt(580), outline: 'none' }} ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onClick={stop}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--ink200)', background: 'var(--head)', display: 'flex', alignItems: 'flex-start', gap: 14, flexShrink: 0 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--ink500)' }}>
              {[deviceRef(dev), dev.storeName].filter(Boolean).join(' · ')}
            </div>
            <h3 id={titleId} style={{ margin: '5px 0 0', fontSize: 16, fontWeight: 700, letterSpacing: '-.02em' }}>Régime et dates</h3>
            <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)', marginTop: 4, lineHeight: 1.5, textWrap: 'pretty' }}>
              Corrigez une erreur d’affectation sans retour au parc. L’échéancier, les prévisions de disponibilité et les alertes d’échéance suivent.
            </div>
          </div>
          <button type="button" className="btn-icon" onClick={onClose} disabled={busy} title="Fermer" aria-label="Fermer">✕</button>
        </div>

        <div style={{ padding: '20px 22px', overflowY: 'auto', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div role="radiogroup" aria-label="Régime" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
            {REGIMES.map((r, i) => {
              const on = regime === r.k;
              return (
                <button key={r.k} type="button" role="radio" aria-checked={on} className={on ? 'row-pick selected' : 'row-pick'} onClick={() => pick(r.k)}
                  {...rovingRadio({ index: i, count: REGIMES.length, checked: on, anyChecked: regime !== null, onPick: (k) => pick(REGIMES[k]!.k) })}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', padding: '13px 14px', color: 'inherit', font: 'inherit', ...(on ? null : { background: 'var(--panel)' }) }}>
                  <span aria-hidden="true" style={{ width: 34, height: 34, borderRadius: 'var(--r-ctrl)', display: 'grid', placeItems: 'center', background: on ? 'var(--ok-bg)' : 'var(--ink100)', color: on ? 'var(--ok-fg)' : 'var(--ink500)' }}>
                    <Icon name={r.icon} size={17} />
                  </span>
                  <span style={{ fontSize: 'var(--t-lead)', fontWeight: 700, marginTop: 9, color: 'var(--ink900)' }}>{r.name}</span>
                  <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', lineHeight: 1.45, marginTop: 3 }}>{r.desc}</span>
                </button>
              );
            })}
          </div>
          {/* ⚠️ DES CHAMPS VIDES SE DISENT : une location affectée avant que ses dates soient exigées n'en a aucune, et
              deux champs vides se lisaient comme un pré-remplissage oublié. */}
          {regime && dated && regime === wasRegime && !wasFrom && !wasTo && (
            <div role="status" style={{ fontSize: 'var(--t-note)', color: 'var(--wa-fg)', lineHeight: 1.5 }}>
              {`Aucune date n’est enregistrée pour cette location : sans ${regime === 'courte' ? 'retour prévu' : 'échéance'}, ni alerte ni blocage automatique ne peuvent la suivre. Saisissez-les ci-dessous.`}
            </div>
          )}
          {regime && dated && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 }}>
              <div style={{ minWidth: 0 }}>
                <label htmlFor={fromId} style={labelSt}>{regime === 'courte' ? 'Début de location' : 'Début du contrat'}</label>
                <input id={fromId} type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={inputSt} />
              </div>
              <div style={{ minWidth: 0 }}>
                <label htmlFor={toId} style={labelSt}>{regime === 'courte' ? 'Retour prévu' : 'Échéance'}</label>
                <input id={toId} type="date" value={to} onChange={(e) => setTo(e.target.value)} style={inputSt} />
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '11px 14px', border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', background: 'var(--in-bg)' }}>
            <span aria-hidden="true" style={{ width: 18, height: 18, borderRadius: 5, background: 'color-mix(in srgb, var(--in-fg) 22%, transparent)', color: 'var(--in-fg)', display: 'grid', placeItems: 'center', fontSize: 'var(--t-meta)', fontWeight: 700, flexShrink: 0, marginTop: 1 }}>i</span>
            <div style={{ fontSize: 'var(--t-note)', color: 'var(--in-fg)', lineHeight: 1.55 }}>{info}</div>
          </div>
        </div>

        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--ink200)', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, background: 'var(--ink-50)', flexShrink: 0 }}>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>Annuler</button>
          <div role={err ? 'alert' : undefined} style={{ flex: '1 1 160px', minWidth: 0, fontSize: 'var(--t-meta)', lineHeight: 1.45, color: err || (problem && regime) ? 'var(--da-fg)' : 'var(--ink500)' }}>{foot}</div>
          <button type="button" className="btn-cta" onClick={() => void save()} disabled={!ready} style={ready ? undefined : ctaOff}>{busy ? 'Enregistrement…' : 'Enregistrer'}</button>
        </div>
      </div>
    </div>
  );
}
